import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Upload, FileText, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { downscaleImage, cloudinaryThumb } from '../utils/imageDownscale';

const CLOUDINARY_CLOUD = process.env.REACT_APP_CLOUDINARY_CLOUD || 'dzlc6qta0';
const CLOUDINARY_PRESET = process.env.REACT_APP_CLOUDINARY_PRESET || 'ievntur_uploads';

const ACCEPTED = '.pdf,.jpg,.jpeg,.png,.heic,.doc,.docx';
const ACCEPTED_MIMES = [
  'application/pdf', 'image/jpeg', 'image/png', 'image/heic',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

/**
 * WorkersCompUploadModal — talent-facing upload widget.
 *
 * Why a dedicated modal: the old "Upload Workers Comp →" link sent the user
 * to a generic /talent/profile screen with no upload field, so the loop was
 * broken. This modal gives them a single-screen flow:
 *   1. See current status (none / pending / approved / rejected with reason)
 *   2. Upload a new doc (image or PDF/DOC)
 *   3. Get instant "Pending review" feedback
 */
export const WorkersCompUploadModal = ({ open, onOpenChange }) => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${baseUrl}/api/talent/workers-comp`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setStatus(await r.json());
    } catch {
      /* noop */
    } finally {
      setLoading(false);
    }
  }, [baseUrl, token]);

  useEffect(() => {
    if (open) fetchStatus();
  }, [open, fetchStatus]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error('File must be under 15 MB');
      return;
    }
    if (file.type && !ACCEPTED_MIMES.includes(file.type)) {
      toast.error('Use PDF, JPG, PNG, HEIC, DOC or DOCX');
      return;
    }

    setUploading(true);
    try {
      const payload = file.type.startsWith('image/')
        ? await downscaleImage(file, { maxEdge: 1800, quality: 0.85 })
        : file;

      const fd = new FormData();
      fd.append('file', payload);
      fd.append('upload_preset', CLOUDINARY_PRESET);
      const isPdfOrDoc = !file.type.startsWith('image/');
      // Cloudinary endpoint: /image/upload for images, /raw/upload for PDFs/DOCs
      const endpoint = isPdfOrDoc
        ? `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/raw/upload`
        : `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`;

      const upRes = await fetch(endpoint, { method: 'POST', body: fd });
      if (!upRes.ok) throw new Error('Cloudinary upload failed');
      const upData = await upRes.json();
      if (!upData.secure_url) throw new Error('Upload returned no URL');

      const persistRes = await fetch(`${baseUrl}/api/talent/workers-comp`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          document_url: upData.secure_url,
          file_name: file.name,
          mime_type: file.type,
        }),
      });
      if (!persistRes.ok) throw new Error('Could not save upload');

      toast.success('Document uploaded — admin will review shortly');
      fetchStatus();
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const renderStatusBadge = () => {
    if (!status) return null;
    if (status.status === 'approved') return (
      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" data-testid="wc-status-approved">
        <CheckCircle2 className="w-3 h-3 mr-1" /> Approved
      </Badge>
    );
    if (status.status === 'pending') return (
      <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid="wc-status-pending">
        <AlertTriangle className="w-3 h-3 mr-1" /> Pending review
      </Badge>
    );
    if (status.status === 'rejected') return (
      <Badge className="bg-red-100 text-red-800 border-red-200" data-testid="wc-status-rejected">
        <XCircle className="w-3 h-3 mr-1" /> Rejected
      </Badge>
    );
    return (
      <Badge className="bg-gray-100 text-gray-700 border-gray-200" data-testid="wc-status-none">
        Not uploaded
      </Badge>
    );
  };

  const previewUrl = status?.document_url
    ? (status.document_url.includes('/image/upload/') ? cloudinaryThumb(status.document_url, 600) : status.document_url)
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="workers-comp-modal">
        <DialogHeader>
          <DialogTitle>Workers Comp Document</DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Upload your active workers compensation certificate. Once an admin
            approves it, the platform adjustment is removed from your earnings.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-cyan-500" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 uppercase tracking-wide">Status</span>
              {renderStatusBadge()}
            </div>

            {status?.status === 'rejected' && status.rejection_reason && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-3" data-testid="wc-rejection-reason">
                <strong>Reason:</strong> {status.rejection_reason}
              </div>
            )}

            {previewUrl && (
              <div className="border border-gray-200 rounded-md overflow-hidden">
                {status.document_url.toLowerCase().includes('.pdf') ||
                 status.document_url.toLowerCase().endsWith('/raw') ? (
                  <a href={status.document_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-3 text-sm text-cyan-600 underline">
                    <FileText className="w-4 h-4" /> View uploaded document
                  </a>
                ) : (
                  <img src={previewUrl} alt="Workers Comp" loading="lazy" decoding="async" className="w-full h-48 object-contain bg-gray-50" />
                )}
              </div>
            )}

            <label className="block">
              <input
                type="file"
                accept={ACCEPTED}
                disabled={uploading}
                onChange={handleFile}
                className="hidden"
                data-testid="wc-file-input"
              />
              <span
                role="button"
                tabIndex={0}
                className={`w-full flex items-center justify-center gap-2 rounded-md py-3 text-sm font-medium border-2 border-dashed transition-colors ${
                  uploading
                    ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                    : 'border-cyan-300 text-cyan-700 hover:bg-cyan-50 cursor-pointer'
                }`}
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading
                  ? 'Uploading…'
                  : status?.status === 'approved'
                    ? 'Replace document'
                    : status?.status === 'pending'
                      ? 'Re-upload'
                      : 'Upload document'}
              </span>
            </label>

            <p className="text-[11px] text-gray-400 leading-snug">
              Accepted: PDF, JPG, PNG, HEIC, DOC, DOCX (max 15&nbsp;MB).
              Images are auto-compressed before upload.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WorkersCompUploadModal;
